import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, TextField, IconButton, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment,
  DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, MenuItem,
} from '@mui/material';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import axios from 'axios';
import { apiUrl, handleApiError } from '../utils/api';
import NoMatchingRecords from '../components/NoMatchingRecords';
import AppDialog from '../components/AppDialog';
import { useConfirm } from '../components/ConfirmProvider';
import { useNotify } from '../components/NotificationProvider';
import { useRole } from '../../auth/RoleContext';
import { codeLabel } from '../../auth/permissions';
import { formatDate } from '../utils/date';

const TRUCK_TYPES = ['ALL', 'BELLY', 'END', 'FLAT', 'TANDEM', 'TRI', 'QUADS', 'SUPER'];

/**
 * Effective-dated Rate Cards.
 * Two ways to manage rates:
 *   - Bulk: upload a weekly CSV (header "Code,Price") for an effective date.
 *   - Single: Add / Edit / Delete individual rates.
 * The table shows the rates currently in effect as of the selected "as of" date.
 */
export default function RateCards() {
  const todayIso = new Date().toISOString().split('T')[0];

  const [cardType, setCardType] = useState('SUB'); // SUB = sub charges; PLANT = plant pays
  const [uploadTruckType, setUploadTruckType] = useState('ALL'); // truck type the uploaded CSV is for
  const [effectiveDate, setEffectiveDate] = useState(todayIso);
  const [asOf, setAsOf] = useState(todayIso);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState(''); // filter the loaded rates by code / truck type

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null => create
  const [form, setForm] = useState({ fsCode: '', rate: '', effectiveDate: todayIso, cardType: 'SUB', truckType: 'ALL' });
  const [selected, setSelected] = useState([]); // ids checked for bulk delete
  const confirm = useConfirm();
  const notify = useNotify();
  const { role } = useRole();
  const codeLbl = codeLabel(role); // 'Job Code' for IVY, else 'FS Code'

  const baseUrl = `${apiUrl}/api/master/rate-cards`;

  const loadRates = async (date) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(baseUrl, { params: { asOf: date, cardType } });
      setRows(Array.isArray(response.data) ? response.data : []);
      setSelected([]);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load rates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRates(asOf); /* eslint-disable-next-line */ }, [asOf, cardType]);

  const handleUpload = async () => {
    if (!file) { setError('Choose a CSV file first.'); return; }
    if (!effectiveDate) { setError('Pick an effective date.'); return; }
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(
        `${baseUrl}/upload?effectiveDate=${effectiveDate}&cardType=${cardType}&truckType=${uploadTruckType}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(response.data);
      setAsOf(effectiveDate);          // view the just-uploaded set
      await loadRates(effectiveDate);
      notify.success('Rate card uploaded');
    } catch (err) {
      const msg = handleApiError(err, 'Upload failed');
      setError(msg);
      notify.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ fsCode: '', rate: '', effectiveDate: asOf || todayIso, cardType, truckType: uploadTruckType });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      fsCode: row.fsCode ?? '',
      rate: row.rate ?? '',
      effectiveDate: row.effectiveDate ?? todayIso,
      cardType: row.cardType ?? cardType,
      truckType: row.truckType ?? 'ALL',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.fsCode?.trim()) { setError(`${codeLbl} is required`); return; }
    if (!form.effectiveDate) { setError('Effective Date is required'); return; }
    const payload = {
      fsCode: form.fsCode.trim(),
      rate: form.rate === '' || form.rate == null ? null : Number(form.rate),
      effectiveDate: form.effectiveDate,
      cardType: form.cardType || cardType,
      truckType: form.truckType || 'ALL',
    };
    try {
      if (editing) {
        await axios.put(`${baseUrl}/${editing.id}`, payload);
      } else {
        await axios.post(baseUrl, payload);
      }
      setDialogOpen(false);
      await loadRates(asOf);
      notify.success(`Rate ${editing ? 'updated' : 'created'}`);
    } catch (err) {
      setError(handleApiError(err, 'Save failed'));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: 'Delete rate',
      message: `Delete the rate for "${row.fsCode}" (effective ${formatDate(row.effectiveDate)})? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${baseUrl}/${row.id}`);
      await loadRates(asOf);
      notify.success('Rate deleted');
    } catch (err) {
      const msg = handleApiError(err, 'Delete failed');
      setError(msg);
      notify.error(msg);
    }
  };

  // Client-side search over the loaded set — matches the code and the truck type (case-insensitive).
  const term = search.trim().toLowerCase();
  const filteredRows = term
    ? rows.filter((r) => `${r.fsCode || ''} ${r.truckType || 'ALL'}`.toLowerCase().includes(term))
    : rows;

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.includes(r.id));
  const someSelected = filteredRows.some((r) => selected.includes(r.id)) && !allSelected;
  const toggleAll = () => setSelected(allSelected ? [] : filteredRows.map((r) => r.id));
  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkRemove = async () => {
    if (selected.length === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.length} rate(s)`,
      message: `Delete ${selected.length} selected rate(s)? This cannot be undone.`,
      confirmText: `Delete ${selected.length}`,
      severity: 'error',
    });
    if (!ok) return;
    setError('');
    const count = selected.length;
    const results = await Promise.allSettled(selected.map((id) => axios.delete(`${baseUrl}/${id}`)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      const msg = `${failed} of ${count} could not be deleted.`;
      setError(msg);
      notify.warning(msg);
    } else {
      notify.success(`${count} rate(s) deleted`);
    }
    await loadRates(asOf);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Rate Cards</Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={cardType}
          onChange={(e, val) => { if (val) setCardType(val); }}
        >
          <ToggleButton value="SUB">Sub Contractor (charges)</ToggleButton>
          <ToggleButton value="PLANT">Plant (pays)</ToggleButton>
          <ToggleButton value="CUSTOMER">Customer (billed)</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {cardType === 'PLANT'
            ? 'Rates the plant pays — used on plant invoices.'
            : cardType === 'CUSTOMER'
              ? 'Rates a customer is billed — used on the field-ticket invoice (e.g. Williams Brothers $250/load).'
              : 'Rates the sub-contractor charges — used on sub invoices & payroll.'}
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload weekly rate card · {cardType === 'PLANT' ? 'Plant' : cardType === 'CUSTOMER' ? 'Customer' : 'Sub Contractor'}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Effective Date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            select
            label="Truck Type"
            value={uploadTruckType}
            onChange={(e) => setUploadTruckType(e.target.value)}
            size="small"
            sx={{ minWidth: 130 }}
            helperText="ALL = default"
          >
            {TRUCK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
            {file ? file.name : 'Choose CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          CSV format: a header row "Code,Price" then one row per rate. Blank prices are stored as no rate.
        </Typography>

        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Effective {formatDate(result.effectiveDate)}: {result.inserted} added, {result.updated} updated, {result.skipped} skipped.
            {result.messages?.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                {result.messages.slice(0, 10).map((m, i) => <li key={i}>{m}</li>)}
                {result.messages.length > 10 && <li>…and {result.messages.length - 10} more</li>}
              </Box>
            )}
          </Alert>
        )}
      </Paper>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Rates in effect</Typography>
        <TextField
          label="As of"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          placeholder={`Search ${codeLbl.toLowerCase()} or truck type…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>,
            endAdornment: search
              ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search"><ClearIcon fontSize="small" /></IconButton></InputAdornment>
              : null,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {term ? `(${filteredRows.length} of ${rows.length} codes)` : `(${rows.length} codes)`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {selected.length > 0 && (
          <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />} onClick={bulkRemove}>
            Delete selected ({selected.length})
          </Button>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Rate</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}
      {!loading && !error && rows.length === 0 && <NoMatchingRecords sx={{ mb: 2 }} />}
      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && <NoMatchingRecords sx={{ mb: 2 }} />}

      {filteredRows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                </TableCell>
                <TableCell>{codeLbl}</TableCell>
                <TableCell>Truck Type</TableCell>
                <TableCell align="right">Rate ($)</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r) => (
                <TableRow key={r.id || r.fsCode} selected={selected.includes(r.id)} hover>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} />
                  </TableCell>
                  <TableCell>{r.fsCode}</TableCell>
                  <TableCell>{r.truckType || 'ALL'}</TableCell>
                  <TableCell align="right">{r.rate != null ? Number(r.rate).toFixed(2) : '—'}</TableCell>
                  <TableCell>{formatDate(r.effectiveDate)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AppDialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Rate' : 'Add Rate'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={form.cardType || 'SUB'}
              onChange={(e, val) => { if (val) setForm({ ...form, cardType: val }); }}
            >
              <ToggleButton value="SUB">Sub Contractor</ToggleButton>
              <ToggleButton value="PLANT">Plant</ToggleButton>
              <ToggleButton value="CUSTOMER">Customer</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              select
              label="Truck Type"
              value={form.truckType || 'ALL'}
              onChange={(e) => setForm({ ...form, truckType: e.target.value })}
              helperText="ALL = default rate (any truck type)"
              fullWidth
            >
              {TRUCK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label={codeLbl}
              value={form.fsCode}
              onChange={(e) => setForm({ ...form, fsCode: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Rate ($)"
              type="number"
              value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              helperText="Leave blank for no rate"
              fullWidth
            />
            <TextField
              label="Effective Date"
              type="date"
              value={form.effectiveDate}
              onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
